import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { LessonReportsSkeleton } from "../SessionListSkeleton";
import { LessonReportCard } from "./LessonReportCard";
import { hasCompletedSession, useLessonReports } from "./useLessonReports";

export const LessonReports = ({ filters }) => {
  const { reports: allReports, error, loading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useLessonReports(filters);
  const reports = allReports.filter(hasCompletedSession);

  const loadMoreRef = useRef(null);

  useEffect(() => {
    const trigger = loadMoreRef.current;
    if (!trigger || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: "200px" },
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (loading) {
    return <LessonReportsSkeleton />;
  }

  if (error) return <EmptyText>{error}</EmptyText>;

  if (reports.length === 0) {
    return <EmptyText>No lesson reports for the selected filters.</EmptyText>;
  }

  return (
    <List>
      {reports.map((card) => (
        <LessonReportCard key={card.assignment_id} card={card} filters={filters} />
      ))}
      {hasNextPage && <LoadMoreTrigger ref={loadMoreRef} aria-hidden="true" />}
      {isFetchingNextPage && <LessonReportsSkeleton rows={1} />}
    </List>
  );
};

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const EmptyText = styled.p`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: #002e19;
  text-align: center;
  padding: 32px 0;
  margin: 0;
`;

const LoadMoreTrigger = styled.div`
  height: 1px;
`;
